import { useEffect, useRef } from "react";
import { MapContainer, TileLayer, useMapEvents, useMap, ZoomControl } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import "@geoman-io/leaflet-geoman-free";
import "@geoman-io/leaflet-geoman-free/dist/leaflet-geoman.css";


// Fix default marker icons (webpack strips them otherwise)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

export function ClickCapture({ onClick }) {
  useMapEvents({
    click(e) {
      if (onClick) onClick(e.latlng.lat, e.latlng.lng);
    },
  });
  return null;
}

// Refit the view whenever `bounds` changes
export function FitBounds({ bounds, nonce }) {
  const map = useMap();
  useEffect(() => {
    if (bounds && bounds.length >= 2) {
      try {
        map.fitBounds(bounds, { padding: [30, 30], maxZoom: 15 });
      } catch (e) {}
    }
  }, [JSON.stringify(bounds), nonce]);
  return null;
}

// Ensure tiles render correctly inside dynamically-sized containers
function InvalidateOnMount() {
  const map = useMap();
  useEffect(() => {
    const t = setTimeout(() => map.invalidateSize(), 150);
    return () => clearTimeout(t);
  }, []);
  return null;
}

export default function LeafletMap({ center = [41.75, -71.3], zoom = 11, height = 420, children, onClick, className = "", satellite = false, zoomPosition = null }) {
  return (
    <div className={`overflow-hidden rounded-xl border border-border ${className}`} style={{ height, maxHeight: "calc(100vh - 96px)" }} data-testid="leaflet-map">
      <MapContainer center={center} zoom={zoom} style={{ height: "100%", width: "100%" }} scrollWheelZoom zoomControl={!zoomPosition}>
        {zoomPosition && <ZoomControl position={zoomPosition} />}
        {satellite ? (
          <TileLayer attribution='Tiles &copy; Esri' url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}" />
        ) : (
          <TileLayer
            attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a>'
            url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
          />
        )}
        <InvalidateOnMount />
        {onClick && <ClickCapture onClick={onClick} />}
        {children}
      </MapContainer>
    </div>
  );
}


// Zillow-style freehand polygon editor (Leaflet-Geoman). Draw by clicking
// points (auto-closes), then drag vertices / the whole shape to refine.
// Distinct, stable color per shop so each territory reads at a glance.
export const ZONE_PALETTE = ["#22D3EE", "#F59E0B", "#A78BFA", "#34D399", "#F472B6", "#60A5FA", "#FB923C", "#4ADE80", "#E879F9", "#2DD4BF", "#FBBF24", "#F87171", "#818CF8", "#C084FC"];
export function colorForShop(shopId) {
  if (!shopId) return "#94A3B8"; // slate — unassigned
  const s = String(shopId);
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) >>> 0;
  return ZONE_PALETTE[h % ZONE_PALETTE.length];
}

export function GeomanEditor({ polygon = [], onChange, color = "#10B981", snapTo = [], snap = true, dragMode = false }) {
  const map = useMap();
  const layerRef = useRef(null);
  const snapLayersRef = useRef([]);
  const cbRef = useRef(onChange);
  cbRef.current = onChange;
  const snapRef = useRef(snap);
  snapRef.current = snap;
  const dragModeRef = useRef(dragMode);
  dragModeRef.current = dragMode;
  const modeRef = useRef(null);
  const snapToRef = useRef(snapTo);
  snapToRef.current = snapTo;

  useEffect(() => {
    if (!map || !map.pm) return;

    const toCoords = (layer) => {
      const ll = layer.getLatLngs();
      const ring = Array.isArray(ll[0]) ? ll[0] : ll;
      return ring.map((p) => [p.lat, p.lng]);
    };
    const emit = (layer) => cbRef.current(toCoords(layer));
    const applyMode = (layer) => {
      if (!layer || !layer.pm) return;
      try {
        if (dragModeRef.current) {
          if (layer.pm.enabled && layer.pm.enabled()) layer.pm.disable();
          if (layer.pm.enableLayerDrag) layer.pm.enableLayerDrag();
        } else {
          if (layer.pm.disableLayerDrag) layer.pm.disableLayerDrag();
          layer.pm.enable({ allowSelfIntersection: false, snappable: snapRef.current });
        }
      } catch (_) {}
    };
    modeRef.current = applyMode;
    const bind = (layer) => {
      layer.on("pm:edit", () => emit(layer));
      layer.on("pm:markerdragend", () => emit(layer));
      layer.on("pm:vertexadded", () => emit(layer));
      layer.on("pm:vertexremoved", () => emit(layer));
      layer.on("pm:dragend", () => emit(layer));
    };

    map.pm.setGlobalOptions({
      snappable: snapRef.current,
      snapDistance: 20,
      snapSegment: true,
      pathOptions: { color, fillColor: color, fillOpacity: 0.2, weight: 2 },
      hintlineStyle: { color, dashArray: [5, 5] },
      templineStyle: { color },
    });
    // Only the polygon-draw tool — no global edit/drag/remove modes, so the
    // read-only <Polygon> layers on the map are never made editable.
    map.pm.addControls({
      position: "topright",
      drawMarker: false, drawCircleMarker: false, drawPolyline: false,
      drawRectangle: false, drawCircle: false, drawText: false,
      drawPolygon: true, editMode: false, dragMode: false, removalMode: false,
      rotateMode: false, cutPolygon: false, oneBlock: true,
    });

    // Invisible snap targets (e.g. bike-path boundary + neighbouring zones) so
    // drawn/edited vertices snap flush against them for gap-free territories.
    (snapToRef.current || []).forEach((ring) => {
      if (ring && ring.length >= 2) {
        const sl = L.polygon(ring, { opacity: 0, fillOpacity: 0, weight: 0, interactive: false });
        sl.addTo(map);
        snapLayersRef.current.push(sl);
      }
    });

    if (polygon && polygon.length >= 3) {
      const layer = L.polygon(polygon, { color, fillColor: color, fillOpacity: 0.2, weight: 2 });
      layer.addTo(map);
      layerRef.current = layer;
      bind(layer);
      applyMode(layer);
    } else {
      // New shape: auto-start the polygon draw tool so the first map click
      // drops a point immediately (no need to hunt for the top-right toolbar).
      try { map.pm.enableDraw("Polygon", { snappable: snapRef.current, allowSelfIntersection: false }); } catch (_) {}
    }

    const handleCreate = (e) => {
      if (e.shape !== "Polygon") return;
      if (layerRef.current) { try { map.removeLayer(layerRef.current); } catch (_) {} }
      layerRef.current = e.layer;
      bind(e.layer);
      applyMode(e.layer);
      emit(e.layer);
    };
    map.on("pm:create", handleCreate);

    return () => {
      map.off("pm:create", handleCreate);
      try { map.pm.disableDraw(); } catch (_) {}
      try { map.pm.removeControls(); } catch (_) {}
      if (layerRef.current) { try { map.removeLayer(layerRef.current); } catch (_) {} layerRef.current = null; }
      snapLayersRef.current.forEach((sl) => { try { map.removeLayer(sl); } catch (_) {} });
      snapLayersRef.current = [];
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Toggle snapping live without tearing down the editor
  useEffect(() => {
    if (map && map.pm) { try { map.pm.setGlobalOptions({ snappable: snap }); } catch (_) {} }
    if (!dragModeRef.current && layerRef.current?.pm?.enabled?.()) { try { layerRef.current.pm.enable({ allowSelfIntersection: false, snappable: snap }); } catch (_) {} }
  }, [map, snap]);

  // Switch between vertex-edit and whole-shape drag without tearing down
  useEffect(() => {
    dragModeRef.current = dragMode;
    if (modeRef.current && layerRef.current) modeRef.current(layerRef.current);
  }, [dragMode]);

  return null;
}

// Admin-only: makes every on-map overlay control (zoom, Geoman draw toolbar,
// custom geometry toolbar) draggable via a small grip handle. Uses CSS
// transform relative to each control's docked position, so corner origin never
// matters. Position is intentionally NOT persisted — it resets on remount.
export function DraggableControls({ selectors = [".leaflet-control-zoom", ".leaflet-pm-toolbar", ".geo-toolbar"] }) {
  const map = useMap();
  useEffect(() => {
    if (!map) return undefined;
    const container = map.getContainer();
    const cleaners = [];

    const makeDraggable = (el) => {
      // Self-healing: only skip if the handle is still present. Some controls
      // (e.g. the geo-toolbar) rebuild their innerHTML on state change, which
      // removes our handle — the MutationObserver then re-adds it here.
      if (!el || el.querySelector(":scope > .ctrl-drag-handle")) return;
      const handle = document.createElement("div");
      handle.className = "ctrl-drag-handle";
      handle.title = "Drag to move";
      handle.setAttribute("data-testid", "ctrl-drag-handle");
      handle.innerHTML = "<span></span><span></span><span></span>";
      el.insertBefore(handle, el.firstChild);

      // Restore any prior drag offset so the control stays put when the handle
      // is recreated after an innerHTML rebuild.
      let tx = parseFloat(el.dataset.dragTx) || 0;
      let ty = parseFloat(el.dataset.dragTy) || 0;
      if (tx || ty) el.style.transform = `translate(${tx}px, ${ty}px)`;
      let startX = 0, startY = 0, baseX = 0, baseY = 0, dragging = false;
      const onMove = (e) => {
        if (!dragging) return;
        tx = baseX + (e.clientX - startX);
        ty = baseY + (e.clientY - startY);
        el.style.transform = `translate(${tx}px, ${ty}px)`;
        el.dataset.dragTx = tx; el.dataset.dragTy = ty;
      };
      const onUp = () => {
        dragging = false;
        try { map.dragging.enable(); } catch (_) { /* noop */ }
        window.removeEventListener("pointermove", onMove);
        window.removeEventListener("pointerup", onUp);
        handle.classList.remove("dragging");
      };
      const onDown = (e) => {
        e.preventDefault(); e.stopPropagation();
        startX = e.clientX; startY = e.clientY; baseX = tx; baseY = ty;
        dragging = true;
        try { map.dragging.disable(); } catch (_) { /* noop */ }
        handle.classList.add("dragging");
        window.addEventListener("pointermove", onMove);
        window.addEventListener("pointerup", onUp);
      };
      handle.addEventListener("pointerdown", onDown);
      L.DomEvent.disableClickPropagation(handle);
      cleaners.push(() => {
        try {
          handle.removeEventListener("pointerdown", onDown);
          window.removeEventListener("pointermove", onMove);
          window.removeEventListener("pointerup", onUp);
          handle.remove();
          el.style.transform = "";
          delete el.dataset.dragTx;
          delete el.dataset.dragTy;
        } catch (_) { /* noop */ }
      });
    };

    const scan = () => selectors.forEach((sel) => container.querySelectorAll(sel).forEach(makeDraggable));
    scan();
    const obs = new MutationObserver(scan);
    obs.observe(container, { childList: true, subtree: true });
    const t1 = setTimeout(scan, 300);
    const t2 = setTimeout(scan, 800);

    return () => {
      obs.disconnect(); clearTimeout(t1); clearTimeout(t2);
      cleaners.forEach((c) => c());
    };
  }, [map]);
  return null;
}

// Geometry helpers shared across panels
export function pointInPolygon(lat, lng, poly) {
  if (!poly || poly.length < 3) return true;
  let inside = false;
  for (let i = 0, j = poly.length - 1; i < poly.length; j = i++) {
    const yi = poly[i][0], xi = poly[i][1], yj = poly[j][0], xj = poly[j][1];
    if (((yi > lat) !== (yj > lat)) && (lng < ((xj - xi) * (lat - yi)) / ((yj - yi) || 1e-12) + xi)) inside = !inside;
  }
  return inside;
}

export function polygonsOverlap(a, b) {
  if (!a?.length || !b?.length) return false;
  if (a.some((p) => pointInPolygon(p[0], p[1], b))) return true;
  if (b.some((p) => pointInPolygon(p[0], p[1], a))) return true;
  return false;
}

export function polygonInside(inner, outer) {
  if (!outer || outer.length < 3) return true;
  return inner.every((p) => pointInPolygon(p[0], p[1], outer));
}

export function polygonAreaM2(poly) {
  if (!poly || poly.length < 3) return 0;
  const R = 6371000, rad = Math.PI / 180;
  const lat0 = (poly.reduce((s, p) => s + p[0], 0) / poly.length) * rad;
  const pts = poly.map((p) => [R * (p[1] * rad) * Math.cos(lat0), R * (p[0] * rad)]);
  let area = 0;
  for (let i = 0; i < pts.length; i++) {
    const [x1, y1] = pts[i], [x2, y2] = pts[(i + 1) % pts.length];
    area += x1 * y2 - x2 * y1;
  }
  return Math.abs(area) / 2;
}

export function polygonCentroid(poly) {
  if (!poly || !poly.length) return null;
  const lat = poly.reduce((s, p) => s + p[0], 0) / poly.length;
  const lng = poly.reduce((s, p) => s + p[1], 0) / poly.length;
  return [lat, lng];
}
